import { decamelizeKeys } from "humps";
import { useActivityPermissions } from "PFApp/activities/hooks";
import { useActivityShowContext } from "PFApp/activities/show/context/activity_show_context";
import { useActivityPageRoles } from "PFApp/activities/show/hooks/use_activity_page_roles";
import { useCurrentActivity } from "PFApp/activities/show/hooks/use_current_activity";
import { TemplateKey } from "PFApp/constants/templates";
import { LoadingDots } from "PFComponents/loading_dots";
import PageTitle from "PFComponents/page/page_title";
import { Tile } from "PFComponents/tile";
import { useIsCurrentUserPermittedTo } from "PFCore/helpers/use_is_permitted_to";
import { useRolesInvalidate } from "PFCore/hooks/queries/roles/use_roles_invalidate";
import { PermissionRule, TableColumn, WorkflowActivity } from "PFTypes";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ActivitiesTable } from "../../../../../../workflow/parts/activities_table";
import css from "./activity_roles.module.scss";
import { NoLinkedRoles } from "./no_linked_roles";
import { RolesActions } from "./roles_actions";

export const ActivityRoles = () => {
  const { t } = useTranslation("activities", { keyPrefix: "roles" });

  const columns: TableColumn[] = useMemo(
    () => [
      { name: "name", title: t("roleTitle"), size: 2, type: "text" },
      { name: "id", title: t("id"), size: 1, type: "text" },
      { name: "status", title: t("status"), size: 1, type: "text" },
      { name: "workflow_state", title: t("workflowState"), size: 2, type: "text" },
      { name: "post_until", title: t("expiryDate"), size: 1, type: "date-time" },
      { name: "availability", title: t("availabilityRequirement"), size: 2, type: "availability" },
      { name: "assignee", title: t("assignee"), size: 2, type: "profile", attribute: ["assignee", "profile"] }
    ],
    [t]
  );

  const activity = useCurrentActivity();
  const { canCreateRoles } = useActivityPermissions(activity);
  const { isEngagement, isAudit } = useActivityShowContext();
  const canBulkManageRoles = useIsCurrentUserPermittedTo(PermissionRule.BulkManageRoles);
  const { invalidateRoles, invalidateAuditRoles } = useRolesInvalidate();
  const { roles, changePage, isLoading, isFetching } = useActivityPageRoles();
  const entries = decamelizeKeys(roles.entries);

  const isClosed = activity.state === "complete";

  const canCreate = !isClosed && isEngagement && canCreateRoles;
  const canBulkManage = isEngagement && canBulkManageRoles;

  if (isLoading) {
    return <LoadingDots centered circlesEnabled />;
  }

  const roleKey = isAudit ? TemplateKey.AuditRole : TemplateKey.Role;

  return (
    <Tile
      variant="light"
      paddingVariant="lg"
      role="tabpanel"
      aria-label={t("tabTitle")}
      qaId="ActivityPage.rolesTab"
    >
      <section className={css.activityRoles}>
        {entries.length === 0 ? (
          <NoLinkedRoles activity={activity} showActions canCreateRoles={canCreate} roleKey={roleKey} />
        ) : (
          <>
            <PageTitle
              actions={
                <RolesActions
                  canBulkManageRoles={canBulkManage}
                  canCreateRoles={canCreate}
                  roleKey={roleKey}
                  activity={activity}
                />
              }
              classes={{ root: css.sticky }}
            >
              {t("tabTitle")}
            </PageTitle>
            <ActivitiesTable
              classes={{ table: css.table }}
              type={roleKey}
              activities={(entries as WorkflowActivity[]) || []}
              columns={columns}
              meta={roles.meta}
              onPageChange={(page) => changePage({ page, perPage: roles.meta.perPage })}
              onRefetch={() => (isAudit ? invalidateAuditRoles() : invalidateRoles())}
              isLoading={isFetching}
            />
          </>
        )}
      </section>
    </Tile>
  );
};
